// src/utils/dealImageHelper.js
import { normalizeImagePath } from "./imageUtils";

const isDirectUrl = (s) =>
  /^https?:\/\//i.test(s) || s.startsWith("blob:") || s.startsWith("data:");

/**
 * ✅ Obtenir l'URL de la première image d'un deal (DEV + PROD)
 */
export const getDealImageUrl = (deal, index = 0, fallback = "/placeholder.jpg") => {
  if (!deal) return fallback;

  // PRIORITÉ 1: imageUrls (backend enrichDealImages)
  if (Array.isArray(deal.imageUrls) && deal.imageUrls.length > index) {
    const url = String(deal.imageUrls[index] || "").trim();
    if (url) return isDirectUrl(url) ? url : normalizeImagePath(url);
  }

  // PRIORITÉ 2: images array
  if (Array.isArray(deal.images) && deal.images.length > index) {
    const img = String(deal.images[index] || "").trim();
    if (img) return isDirectUrl(img) ? img : normalizeImagePath(img);
  }

  // PRIORITÉ 3: images string (JSON ou comma-separated)
  if (typeof deal.images === "string" && deal.images.trim()) {
    const raw = deal.images.trim();

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > index) {
        const img = String(parsed[index] || "").trim();
        if (img) return isDirectUrl(img) ? img : normalizeImagePath(img);
      }
    } catch {
      const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
      if (parts.length > index) {
        const img = parts[index];
        return isDirectUrl(img) ? img : normalizeImagePath(img);
      }
    }
  }

  // PRIORITÉ 4: image
  if (typeof deal.image === "string" && deal.image.trim()) {
    const img = deal.image.trim();
    return isDirectUrl(img) ? img : normalizeImagePath(img);
  }

  // PRIORITÉ 5: thumbnail
  if (typeof deal.thumbnail === "string" && deal.thumbnail.trim()) {
    const img = deal.thumbnail.trim();
    return isDirectUrl(img) ? img : normalizeImagePath(img);
  }

  // PRIORITÉ 6: mainImage
  if (typeof deal.mainImage === "string" && deal.mainImage.trim()) {
    const img = deal.mainImage.trim();
    return isDirectUrl(img) ? img : normalizeImagePath(img);
  }

  return fallback;
};

/**
 * ✅ Obtenir toutes les images (DEV + PROD)
 */
export const getAllDealImageUrls = (deal, fallback = "/placeholder.jpg") => {
  if (!deal) return [fallback];

  // 1) imageUrls
  if (Array.isArray(deal.imageUrls) && deal.imageUrls.length > 0) {
    const urls = deal.imageUrls
      .map((u) => String(u || "").trim())
      .filter(Boolean)
      .map((u) => (isDirectUrl(u) ? u : normalizeImagePath(u)))
      .filter(Boolean);

    if (urls.length) return urls;
  }

  // 2) images array
  if (Array.isArray(deal.images) && deal.images.length > 0) {
    const urls = deal.images
      .map((u) => String(u || "").trim())
      .filter(Boolean)
      .map((u) => (isDirectUrl(u) ? u : normalizeImagePath(u)))
      .filter(Boolean);

    if (urls.length) return urls;
  }

  // 3) images string
  if (typeof deal.images === "string" && deal.images.trim()) {
    const raw = deal.images.trim();

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        const urls = parsed
          .map((u) => String(u || "").trim())
          .filter(Boolean)
          .map((u) => (isDirectUrl(u) ? u : normalizeImagePath(u)))
          .filter(Boolean);

        if (urls.length) return urls;
      }
    } catch {
      const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
      if (parts.length) {
        const urls = parts
          .map((u) => (isDirectUrl(u) ? u : normalizeImagePath(u)))
          .filter(Boolean);
        if (urls.length) return urls;
      }
    }
  }

  // 4) image / thumbnail fallback
  if (typeof deal.image === "string" && deal.image.trim()) {
    const u = deal.image.trim();
    return [isDirectUrl(u) ? u : normalizeImagePath(u)].filter(Boolean);
  }

  if (typeof deal.thumbnail === "string" && deal.thumbnail.trim()) {
    const u = deal.thumbnail.trim();
    return [isDirectUrl(u) ? u : normalizeImagePath(u)].filter(Boolean);
  }

  return [fallback];
};

export const dealHasImages = (deal) => {
  if (!deal) return false;
  if (Array.isArray(deal.imageUrls) && deal.imageUrls.length) return true;
  if (Array.isArray(deal.images) && deal.images.length) return true;
  if (typeof deal.images === "string" && deal.images.trim()) return true;
  if (deal.image) return true;
  if (deal.thumbnail) return true;
  if (deal.mainImage) return true;
  return false;
};

export const getDealImageCount = (deal) => {
  if (!deal) return 0;
  if (Array.isArray(deal.imageUrls)) return deal.imageUrls.length;
  if (Array.isArray(deal.images)) return deal.images.length;

  if (typeof deal.images === "string" && deal.images.trim()) {
    try {
      const parsed = JSON.parse(deal.images);
      if (Array.isArray(parsed)) return parsed.length;
    } catch {
      return deal.images.split(",").map((s) => s.trim()).filter(Boolean).length;
    }
  }

  if (deal.image || deal.thumbnail || deal.mainImage) return 1;
  return 0;
};

export default getDealImageUrl;
